import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import type {
  ActivationBonusBackfillRepository,
  BackfillCandidate,
  BackfillDisposition,
  ResolvedBackfill,
} from "./activation_bonus_backfill_bloc";
import { isTerminal } from "./job_polling";
import type { JobRecord, JobStatus } from "./types";

const client = generateClient<Schema>();

const JOB_TYPE = "activation-bonus-backfill";

const DISPOSITIONS: BackfillDisposition[] = [
  "will_pay",
  "will_revive",
  "already_paid",
  "paid_externally",
  "wrong_stage",
  "no_merchant",
  "no_business",
  "no_destination",
  "duplicate",
  "lookup_failed",
];

/**
 * Unrecognised values fall back to `lookup_failed` rather than `will_pay`: a
 * disposition this client does not understand must never be read as approval
 * to send money.
 */
function normalizeDisposition(value: unknown): BackfillDisposition {
  if (typeof value !== "string") return "lookup_failed";
  return DISPOSITIONS.includes(value as BackfillDisposition)
    ? (value as BackfillDisposition)
    : "lookup_failed";
}

function toCandidate(raw: unknown): BackfillCandidate {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    email: String(r["email"] ?? ""),
    userId: typeof r["userId"] === "string" ? r["userId"] : null,
    businessId: typeof r["businessId"] === "string" ? r["businessId"] : null,
    businessName:
      typeof r["businessName"] === "string" ? r["businessName"] : null,
    stage: typeof r["stage"] === "string" ? r["stage"] : null,
    hasDestination: r["hasDestination"] === true,
    disposition: normalizeDisposition(r["disposition"]),
    detail: typeof r["detail"] === "string" ? r["detail"] : null,
  };
}

function toJobRecord(raw: Schema["Job"]["type"]): JobRecord {
  return {
    id: raw.id,
    type: raw.type,
    status: (raw.status ?? "pending") as JobStatus,
    triggeredBy: raw.triggeredBy,
    total: raw.total ?? null,
    processed: raw.processed ?? 0,
    counters:
      typeof raw.counters === "string"
        ? raw.counters
        : raw.counters != null
          ? JSON.stringify(raw.counters)
          : null,
    result:
      typeof raw.result === "string"
        ? raw.result
        : raw.result != null
          ? JSON.stringify(raw.result)
          : null,
    error: raw.error ?? null,
    startedAt: raw.startedAt ?? null,
    completedAt: raw.completedAt ?? null,
  };
}

export const activationBonusBackfillRepository: ActivationBonusBackfillRepository =
  {
    async resolve(emails, reviveClosedTracks) {
      const { data, errors } =
        await client.queries.ResolveActivationBonusBackfill({
          input: { emails, reviveClosedTracks },
        });

      if (errors?.length) throw new Error(errors[0].message);
      if (!data) throw new Error("Empty response from resolve");

      const d = data as Record<string, unknown>;
      const candidates = ((d["candidates"] as unknown[]) ?? [])
        .filter((row): row is NonNullable<typeof row> => row !== null)
        .map(toCandidate);

      const resolved: ResolvedBackfill = {
        candidates,
        duplicates: ((d["duplicates"] as unknown[]) ?? []).filter(
          (v): v is string => typeof v === "string",
        ),
        payableCount:
          typeof d["payableCount"] === "number" ? d["payableCount"] : 0,
        reviveCount:
          typeof d["reviveCount"] === "number" ? d["reviveCount"] : 0,
        totalAmountCents:
          typeof d["totalAmountCents"] === "number" ? d["totalAmountCents"] : 0,
      };

      return resolved;
    },

    async kickoff(emails, reviveClosedTracks) {
      const result = await client.mutations.enqueueJob({
        type: JOB_TYPE,
        // The flag has to travel with the job: the runner re-resolves every
        // merchant, so a previewed `will_revive` resolves to `wrong_stage`
        // without it and the run pays nobody.
        input: JSON.stringify({ emails, reviveClosedTracks }),
      });
      if (result.errors?.length) throw new Error(result.errors[0].message);
      if (!result.data?.jobId)
        throw new Error("No jobId returned from enqueueJob");
      return result.data.jobId;
    },

    async findRunningJob() {
      const result = await client.models.Job.list({
        filter: {
          type: { eq: JOB_TYPE },
          or: [{ status: { eq: "running" } }, { status: { eq: "pending" } }],
        },
      });
      if (result.errors?.length) throw new Error(result.errors[0].message);
      const first = result.data[0];
      return first ? toJobRecord(first) : null;
    },

    async cancelJob(jobId) {
      const current = await client.models.Job.get({ id: jobId });
      if (current.errors?.length) throw new Error(current.errors[0].message);
      const status = current.data?.status;
      if (status !== "running" && status !== "pending") return;
      const result = await client.models.Job.update({
        id: jobId,
        status: "cancelled",
      });
      if (result.errors?.length) throw new Error(result.errors[0].message);
    },

    subscribeToJob(jobId, onUpdate, onError) {
      let cancelled = false;
      let sawTerminal = false;

      const deliver = (record: JobRecord) => {
        if (cancelled) return;
        if (isTerminal(record.status)) sawTerminal = true;
        onUpdate(record);
      };

      const sub = client.models.Job.onUpdate({
        filter: { id: { eq: jobId } },
      }).subscribe({
        next: (data) => deliver(toJobRecord(data)),
        error: (err: unknown) => {
          onError(err instanceof Error ? err : new Error(String(err)));
        },
      });

      // The subscription only delivers future updates. A short run — and a
      // backfill of a handful of emails is short — can reach a terminal status
      // before it attaches, leaving the sheet showing "pending" forever. Read
      // the row once to reconcile whatever was missed.
      void client.models.Job.get({ id: jobId })
        .then(({ data, errors }) => {
          if (cancelled) return;
          if (errors?.length) {
            onError(new Error(errors[0].message));
            return;
          }
          if (!data) return;
          const record = toJobRecord(data);
          // This read raced the subscription and may be the older of the two.
          // Forwarding a stale non-terminal snapshot after a terminal update
          // would put the sheet back to watching a job that will never emit
          // again — the exact hang this read exists to prevent.
          if (sawTerminal && !isTerminal(record.status)) return;
          deliver(record);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          onError(err instanceof Error ? err : new Error(String(err)));
        });

      return () => {
        cancelled = true;
        sub.unsubscribe();
      };
    },
  };
